// @ts-check
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import UnsavedChangesPrompt from '../components/UnsavedChangesPrompt';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';

/**
 * App-wide registry of "does anything currently have unsaved changes".
 *
 * React Router's useBlocker supports only one active blocker per router at a
 * time, so this app must never call useUnsavedChangesGuard from more than one
 * place. Instead, any page/modal/inline form registers its own local dirty
 * boolean here via useRegisterUnsavedChanges, and exactly one guard —
 * UnsavedChangesGuardMount, mounted once in AdminLayout — reacts to the OR of
 * everything currently registered.
 */
const UnsavedChangesContext = createContext(
  /** @type {{ register: (key: symbol, isDirty: boolean) => void, unregister: (key: symbol) => void, hasAnyUnsavedChanges: boolean } | null} */ (
    null
  ),
);

export const UnsavedChangesProvider = ({ children }) => {
  const [registry, setRegistry] = useState(() => new Map());

  const register = useCallback((key, isDirty) => {
    setRegistry((prev) => {
      if (prev.get(key) === isDirty) return prev;
      const next = new Map(prev);
      next.set(key, isDirty);
      return next;
    });
  }, []);

  const unregister = useCallback((key) => {
    setRegistry((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const hasAnyUnsavedChanges = useMemo(() => [...registry.values()].some(Boolean), [registry]);

  const value = useMemo(
    () => ({ register, unregister, hasAnyUnsavedChanges }),
    [register, unregister, hasAnyUnsavedChanges],
  );

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
};

/**
 * Registers the caller's own local dirty boolean into the app-wide registry.
 * Call from any page, modal, or inline form — no need to know about routing
 * or the single-blocker constraint. Unregisters automatically on unmount.
 *
 * @param {boolean} isDirty
 */
export function useRegisterUnsavedChanges(isDirty) {
  const ctx = useContext(UnsavedChangesContext);
  const keyRef = useRef(/** @type {symbol | null} */ (null));
  if (keyRef.current === null) keyRef.current = Symbol('unsaved-changes-registrant');

  useEffect(() => {
    if (!ctx) return undefined;
    ctx.register(keyRef.current, isDirty);
    return () => ctx.unregister(keyRef.current);
  }, [ctx, isDirty]);
}

/**
 * Reads the current aggregate "is anything dirty" boolean — for a parent
 * component that needs to guard its OWN internal navigation (e.g. switching
 * between in-page tabs that mount/unmount child forms), not for arming the
 * app-wide router/beforeunload guard (that's UnsavedChangesGuardMount's job,
 * mounted once in AdminLayout).
 *
 * @returns {boolean}
 */
export function useHasAnyUnsavedChanges() {
  const ctx = useContext(UnsavedChangesContext);
  return ctx?.hasAnyUnsavedChanges ?? false;
}

/**
 * Reads the aggregated dirty state, arms the single app-wide navigation
 * guard, and renders the confirmation modal. Mounted exactly once, in
 * AdminLayout.jsx — never call this (or useUnsavedChangesGuard directly)
 * from anywhere else.
 */
export const UnsavedChangesGuardMount = () => {
  const ctx = useContext(UnsavedChangesContext);
  const blocker = useUnsavedChangesGuard(ctx?.hasAnyUnsavedChanges ?? false);
  return <UnsavedChangesPrompt blocker={blocker} />;
};
